import { useCallback, useEffect, useState } from "react";
import cx from "clsx";
import {
  createExport,
  getProducts,
  optiviseProducts,
  deleteProducts,
} from "../api";
import {
  Alert,
  Anchor,
  Button,
  Center,
  Checkbox,
  Container,
  Group,
  List,
  ListItem,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import { Link } from "wouter";
import {
  IconAlertHexagon,
  IconAlertHexagonFilled,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconFileTypeCsv,
  IconFileTypeXls,
  IconInfoCircle,
  IconSearch,
  IconSelector,
  IconTrash,
  IconWand,
} from "@tabler/icons-react";
import { usePolling } from "../hooks/usePolling";

import classes from "./ProductList.module.css";
import QualityBadge from "./QualityBadge";
import ProductStatusBadge from "./ProductStatusBadge";
import { notifications } from "@mantine/notifications";
import { ListingStatusTooltip } from "../Imports/StatusTooltip";
import { useStore } from "../store";
import { useMarketplaces } from "../_Shared/useMarketplaces";
import { destinationName } from "../utils/skus";
import { confirmBeforeUploadCopy } from "../utils/merchantCopy";
import CatalogueDefaultsBar from "./CatalogueDefaultsBar";

function Th({ children, reversed, sorted, onSort, ta }) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <Table.Th ta={ta} className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group gap={4} justify="space-between" wrap="nowrap">
          <Text fw={500} fz="sm">
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon size={16} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function filterData(data, search) {
  const rows = data ?? [];
  const query = search.toLowerCase().trim();
  if (!query) return rows;
  return rows.filter((item) => {
    return (
      (item.name ?? item.fieldValues?.name)?.toLowerCase().includes(query) ||
      (item.productIdentifier ?? item.fieldValues?.ean)?.includes(query)
    );
  });
}

function sortData(data, payload) {
  const { sortBy } = payload;
  const rows = data ?? [];

  if (!sortBy) {
    return filterData(rows, payload.filter);
  }

  if (sortBy === "issues") {
    return filterData(
      [...rows].sort((a, b) =>
        payload.reversed
          ? (a.validationFailures?.length ?? 0) -
            (b.validationFailures?.length ?? 0)
          : (b.validationFailures?.length ?? 0) -
            (a.validationFailures?.length ?? 0)
      ),
      payload.filter
    );
  }

  if (
    sortBy === "idCreated" ||
    sortBy === "status" ||
    sortBy === "flags" ||
    sortBy === "qualityScore" ||
    sortBy === "originalQualityScore"
  ) {
    return filterData(
      [...rows].sort((a, b) => {
        if (payload.reversed) {
          return (b[sortBy] + "").localeCompare(a[sortBy] + "");
        }

        return (a[sortBy] + "").localeCompare(b[sortBy] + "");
      }),
      payload.filter
    );
  } else {
    return filterData(
      [...rows].sort((a, b) => {
        if (payload.reversed) {
          return b.fieldValues?.[sortBy]?.localeCompare(a.fieldValues?.[sortBy]);
        }

        return a.fieldValues?.[sortBy]?.localeCompare(b.fieldValues?.[sortBy]);
      }),
      payload.filter
    );
  }
}

function ErrorTooltip({ validationFailures }) {
  return (
    <>
      Issues
      <List
        size="md"
        spacing="xs"
        center
        icon={<IconAlertHexagonFilled size={18} color="red" />}
      >
        {(validationFailures ?? []).map((error) => (
          <ListItem key={error.field + error.type}>
            <Text span fz="sm" fw={600}>
              '{error.field}':
            </Text>
            <Text span fz="sm">
              {error.message}
            </Text>
          </ListItem>
        ))}
      </List>
    </>
  );
}

export default function ProductsList({ params }) {
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { marketplaces } = useMarketplaces();
  const dest = destinationName(tenantProfile, marketplaces);
  const [products, setProducts] = useState(null);

  const [filter, setFilter] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState("issues");
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const pageSize = 100;

  // Check if any products are in "processing" state
  const hasProcessingItems = products?.data?.some((product) =>
    String(product.flags ?? "")
      .toLowerCase()
      .includes("processing")
  );

  const loadProducts = useCallback(() => {
    getProducts(activePage, pageSize, params.mappingId).then((data) => {
      setProducts(data);
      setSortedData(
        sortData(data?.data, {
          sortBy,
          reversed: reverseSortDirection,
          filter,
        })
      );
    });
  }, [activePage, params.mappingId, sortBy, reverseSortDirection, filter]);

  useEffect(() => {
    getProducts(activePage, pageSize, params.mappingId).then((data) => {
      setProducts(data);
      setSortedData(
        sortData(data?.data, { sortBy, reversed: reverseSortDirection, filter })
      );
    });
  }, [activePage, params.mappingId, params?.importId]);

  usePolling(loadProducts, {
    enabled: products != null,
    intervalMs: hasProcessingItems ? 5_000 : 60_000,
    invokeImmediately: false,
  });

  const [selection, setSelection] = useState([]);
  const pageItems = products?.data ?? [];
  const actionItems =
    selection.length > 0
      ? pageItems.filter((p) => selection.includes(p.id))
      : pageItems;
  const allOptivised =
    actionItems.length > 0 &&
    actionItems.every((p) =>
      String(p.flags ?? "").toLowerCase().includes("optivised"),
    );
  const optiviseLabel = allOptivised ? "Re-Optivise" : "Optivise";
  const toggleRow = (id) =>
    setSelection((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  const toggleAll = () =>
    setSelection((current) =>
      current.length === pageItems.length
        ? []
        : pageItems.map((item) => item.id)
    );

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(
      sortData(products?.data, { sortBy: field, reversed, filter })
    );
  };

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setFilter(value);
    // console.log("SEARCH: ", value);
    setSortedData(
      sortData(products?.data, {
        sortBy,
        reversed: reverseSortDirection,
        filter: value,
      })
    );
  };

  const optiviseSelected = () => {
    setIsExporting(true);

    optiviseProducts(params.mappingId, { ids: selection, title: "tbd" })
      .then((data) => {
        // Server will lock the products and set them to processing, so update UI:
        setProducts((current) => {
          const newData = {
            ...current,
            data: current.data.map((product) => {
              var newItem = data.find((item) => item.id === product.id);
              return newItem ?? product;
            }),
          };
          return newData;
        });
        notifications.show({
          title: "Optivise started!",
          message:
            "The selected products are being optivised! We'll notify you by email when it's done.",
          autoClose: 20_000,
        });
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const deleteSelected = () => {
    setIsExporting(true);
    setDeleteModalOpen(false);

    deleteProducts({ ids: selection, mappingId: params.mappingId })
      .then(() => {
        // Remove deleted products from the list
        setProducts((current) => {
          const newData = {
            ...current,
            data: current.data.filter(
              (product) => !selection.includes(product.id)
            ),
            totalCount: current.totalCount - selection.length,
          };
          return newData;
        });
        setSelection([]);
        notifications.show({
          title: "Products deleted!",
          message: `${selection.length} product(s) have been deleted.`,
          autoClose: 5000,
        });
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const exportCount =
    selection.length > 0 ? selection.length : products?.totalCount;

  const exportFile = async (format) => {
    setIsExporting(true);
    try {
      await createExport(params?.mappingId, {
        ids: selection,
        format,
      });
    } finally {
      setIsExporting(false);
    }
  };

  let rows = sortedData?.map((product) => {
    const selected = selection.includes(product.id);
    return (
      <Table.Tr
        className={cx({ [classes.rowSelected]: selected })}
        key={product.id}
      >
        <Table.Td>
          <Checkbox
            onClick={(e) => e.stopPropagation()}
            checked={selection.includes(product.id)}
            onChange={() => toggleRow(product.id)}
          />
        </Table.Td>
        <Table.Td>
          <Anchor component={Link} to={`/products/${product.id}`}>
            {product.productIdentifier ?? product.fieldValues?.ean}
          </Anchor>
        </Table.Td>
        <Table.Td>
          {String(product.flags ?? "")
            .split(",")
            .filter(Boolean)
            .map((flag) => (
            <ProductStatusBadge
              key={product.id + "status" + flag}
              status={flag.trim()}
            />
          ))}
        </Table.Td>
        <Table.Td ta="right">
          {product.validationFailures?.length > 0 ? (
            <Tooltip
              maw="40%"
              multiline
              label={
                <ErrorTooltip validationFailures={product.validationFailures} />
              }
              position="top"
              withArrow
            >
              <Group gap={6} align="center" justify="flex-end">
                <IconAlertHexagon size={20} color="red" />
                {product.validationFailures?.length}
              </Group>
            </Tooltip>
          ) : (
            <Text c="dimmed" ta="right">
              0
            </Text>
          )}
        </Table.Td>

        {/* TBD: We should have information about errors / validations here! */}
        <Table.Td>
          <Timeago date={product.idCreated} />
        </Table.Td>
        <Table.Td>
          <Anchor component={Link} to={`/products/${product.id}`}>
            {product.name ?? product.fieldValues?.name ?? "(No name)"}
          </Anchor>
        </Table.Td>
        <Table.Td>
          {(product.category ?? product.fieldValues?.category)
            ?.split("/")
            .splice(-1)}
        </Table.Td>
        <Table.Td ta="right">
          <QualityBadge score={product.qualityScore} />
        </Table.Td>
        <Table.Td ta="right">
          <QualityBadge score={product.originalQualityScore} />
        </Table.Td>
      </Table.Tr>
    );
  });

  rows ??= []; // avoid NREs

  if (rows.length === 0 && !products) {
    rows = (
      <Table.Tr>
        <Table.Td colSpan={9}>
          <Stack align="center" justify="center" mih={80}>
            <Text ta="center" c="dimmed">
              Loading...
            </Text>
            <Loader />
          </Stack>
        </Table.Td>
      </Table.Tr>
    );
  }

  if (rows.length === 0) {
    rows = (
      <Table.Tr>
        <Table.Td colSpan={9}>
          <Text ta="center" c="dimmed">
            No products found
          </Text>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Container size="xl">
      <Stack>
        {params.importId && (
          <Anchor onClick={() => window.history.back()} c="gray">
            ← Back
          </Anchor>
        )}
        <Title order={1}>Review & Download</Title>
        <Text c="dimmed" mt={4}>
          {products?.totalCount ?? 0} products. Check issues before you export.
        </Text>
      </Stack>

      <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />} mt="md">
        {confirmBeforeUploadCopy(dest)}
      </Alert>
      <CatalogueDefaultsBar
        mappingId={params.mappingId}
        onApplied={loadProducts}
      />

      <Group my="md" align="flex-end" justify="space-between">
        <Group>
          <Pagination
            hideWithOnePage={false}
            total={Math.max(1, Math.ceil(products?.totalCount / pageSize))}
            value={activePage}
            onChange={setActivePage}
          />

          <TextInput
            placeholder="Filter"
            leftSection={<IconSearch size={16} stroke={1.5} />}
            value={filter}
            onChange={handleSearchChange}
            // value={filter}
            // onChange={(event) => setFilter(event.currentTarget.value)}
          />
        </Group>

        <Group>
          <Button
            disabled={selection.length === 0}
            loading={isExporting}
            size="sm"
            color="red"
            variant="outline"
            onClick={() => setDeleteModalOpen(true)}
            leftSection={<IconTrash size={18} stroke={1.5} />}
          >
            Delete ({selection.length})
          </Button>
          <Button
            loading={isExporting}
            size="sm"
            variant="default"
            onClick={optiviseSelected}
            leftSection={<IconWand size={18} stroke={1.5} />}
          >
            {optiviseLabel} (
            {selection.length > 0 ? selection.length : products?.totalCount})
          </Button>
          <Button.Group>
            <Button
              loading={isExporting}
              aria-label="Export to XLSX"
              leftSection={<IconDownload size={16} stroke={1.5} />}
              rightSection={<IconFileTypeXls size={16} stroke={1.5} />}
              onClick={() => exportFile("xlsx")}
            >
              .xlsx ({exportCount})
            </Button>
            <Button
              variant="default"
              loading={isExporting}
              aria-label="Export to CSV"
              onClick={() => exportFile("csv")}
              leftSection={<IconDownload size={16} stroke={1.5} />}
              rightSection={<IconFileTypeCsv size={16} stroke={1.5} />}
            >
              .csv ({exportCount})
            </Button>
          </Button.Group>
        </Group>
      </Group>

      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Checkbox
                onChange={toggleAll}
                disabled={pageItems.length === 0}
                checked={
                  pageItems.length > 0 && selection.length === pageItems.length
                }
                indeterminate={
                  selection.length > 0 &&
                  selection.length !== pageItems.length
                }
              />
            </Table.Th>
            <Th
              sorted={sortBy === "ean"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("ean")}
            >
              Identifier
            </Th>
            <Th
              sorted={sortBy === "flags"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("flags")}
            >
              <Tooltip label={<ListingStatusTooltip />}>
                <span>
                  Status&nbsp;
                  <IconInfoCircle size={16} stroke={1.5} />
                </span>
              </Tooltip>
            </Th>

            <Th
              sorted={sortBy === "issues"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("issues")}
            >
              <Tooltip label="SKUs with flagged issues will be rejected by Mirakl. You must fix all errors before uploading to Mirakl.">
                <span>
                  Issues&nbsp;
                  <IconInfoCircle size={16} stroke={1.5} />
                </span>
              </Tooltip>
            </Th>

            <Th
              sorted={sortBy === "idCreated"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("idCreated")}
            >
              Created
            </Th>

            <Th
              sorted={sortBy === "name"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("name")}
            >
              Name
            </Th>

            <Th
              sorted={sortBy === "category"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("category")}
            >
              Category
            </Th>

            <Th
              ta="right"
              sorted={sortBy === "qualityScore"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("qualityScore")}
            >
              Current
            </Th>

            <Th
              ta="right"
              sorted={sortBy === "originalQualityScore"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("originalQualityScore")}
            >
              Original
            </Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Deletion"
      >
        <Text>
          Are you sure you want to delete {selection.length} product(s)? This
          action cannot be undone.
        </Text>
        <Group mt="md" justify="flex-end">
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button color="red" onClick={deleteSelected}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}
